/**
 * `development_plans` の「表の1行に表示する計画」選定ロジック (純粋関数)。
 * docs/design/v05-skill-registration-table-redesign-detail.md §4-3。
 *
 * development_plans は (employee_id, skill_id) に UNIQUE 制約が無く、status 違いで複数行が
 * 併存しうる。サーバー側に ORDER BY 実装は無いため、このロジックが「唯一の選定ロジック」になる。
 */

export type DevelopmentPlanStatus = 'active' | 'pending_judgment' | 'achieved' | 'not_achieved' | 'cancelled';

export type DevelopmentPlanForSelection = {
  plan_id: string;
  status: DevelopmentPlanStatus;
  due_date: string | null;
  created_at: string;
};

export type DevelopmentPlanSelection<T extends DevelopmentPlanForSelection = DevelopmentPlanForSelection> =
  | { kind: 'editable'; plan: T }
  | { kind: 'readonly_result'; plan: T }
  | { kind: 'none' };

/** §4-3 の比較関数。due_date が無い計画は「緊急度が低い」= 最後尾に回す (SQLiteの既定 ORDER BY を
 *  そのまま使うと NULL が先頭に来て誤って最優先表示されるバグの再発防止)。due_date が同値
 *  (NULL 同士を含む) なら created_at 降順 (新しい方が先) をタイブレークに使う。 */
export function compareDevelopmentPlanCandidates(
  a: DevelopmentPlanForSelection, b: DevelopmentPlanForSelection,
): number {
  if (a.due_date === null && b.due_date !== null) return 1;
  if (b.due_date === null && a.due_date !== null) return -1;
  if (a.due_date !== null && b.due_date !== null && a.due_date !== b.due_date) {
    return a.due_date < b.due_date ? -1 : 1;
  }
  // 同値 (両方 due_date あり同値、または両方 null) → created_at 降順
  if (a.created_at === b.created_at) return 0;
  return a.created_at > b.created_at ? -1 : 1;
}

/** 対象社員×スキルの development_plans 一覧から、表の1行に表示する計画を1件選定する。
 *  1. active/pending_judgment が1件以上あれば compareDevelopmentPlanCandidates の先頭を採用 (editable)
 *  2. 該当なしで achieved/not_achieved があれば created_at 降順で最新の1件 (readonly_result)
 *  3. どちらも無ければ none (新規作成モード) */
export function selectCurrentDevelopmentPlan<T extends DevelopmentPlanForSelection>(
  plans: T[],
): DevelopmentPlanSelection<T> {
  const activeCandidates = plans.filter((p) => p.status === 'active' || p.status === 'pending_judgment');
  if (activeCandidates.length > 0) {
    const sorted = [...activeCandidates].sort(compareDevelopmentPlanCandidates);
    return { kind: 'editable', plan: sorted[0]! };
  }
  const resultCandidates = plans.filter((p) => p.status === 'achieved' || p.status === 'not_achieved');
  if (resultCandidates.length > 0) {
    const sorted = [...resultCandidates].sort((a, b) => {
      if (a.created_at === b.created_at) return 0;
      return a.created_at > b.created_at ? -1 : 1;
    });
    return { kind: 'readonly_result', plan: sorted[0]! };
  }
  return { kind: 'none' };
}
